import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import '@adornis/charts/chartjs/line-chart.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flat-loader.js';
import { LOADER } from '@adornis/chemistry/elements/components/x-loader.js';
import type { DataViewOptions, FullDataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { shouldIncludeFieldHelpers } from '@adornis/field-management/field-management-options.js';
import { AbstractAspect } from '@adornis/filter/aspect-schemas/AbstractAspect.js';
import { BooleanAspect, BooleanAspectSchema } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { DateTimeAspectSchema } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import type { FilterEditorOptions, FullFilterEditorOptions } from '@adornis/filter/filter-options.js';
import { ProductLKK } from '@chancenwerk/_cms/products/entities/ProductLKK.js';
import { CampusLevumiLevel } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiLevel.js';
import { buildChancenDataViewOptions } from '@chancenwerk/client/design/components/cr-data-view.js';
import '@chancenwerk/components/semantics/chancen-component-semantic-kpf-picker.js';
import { LevumiMathCategory, LevumiSubject } from '@chancenwerk/db/enums/CampusLevumiEnums.js';
import { ExerciseType } from '@chancenwerk/db/enums/enums.js';
import {
  ContentResultEventlog,
  getLatestExerciseResultForUser,
} from '@chancenwerk/db/eventlogs/ContentResultEventlog.js';
import { ChancenwerkUserFilter } from '@chancenwerk/db/filter/ChancenwerkUserFilter.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-accordeon.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-card.js';
import '@chancenwerk/design/components/cr-flat-loader.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-grid.js';
import '@chancenwerk/design/components/cr-icon.js';
import { CrPage } from '@chancenwerk/design/components/cr-page.js';
import '@chancenwerk/design/components/cr-tooltip.js';
import { KPFAspectSchema } from '@chancenwerk/master-data-management/kpf/entities/aspects/KPFAspect.js';
import { KPFMultipleAspectSchema } from '@chancenwerk/master-data-management/kpf/entities/aspects/KPFMultipleAspect.js';
import { UserAPI } from '@chancenwerk/master-data-management/users/api/UserAPI.js';
import { STUDENT_BLACKLIST, Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { ChancenPersonalConfigAPI } from '@chancenwerk/user-configs/api/ChancenPersonalConfigAPI.js';
import { ListUIConfig } from '@chancenwerk/user-configs/entities/ListUIConfig.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { DateTime } from 'luxon';
import { switchMap } from 'rxjs';
import { CampusRoutes } from './../routes.js';
import './campus-page-student-knowledge-diagnostik-details.js';

const eventlogSelectionSet: BaseQLSelectionSet<ContentResultEventlog> = {
  type: 1,
  createdAt: 1,
  levumiResultInformation: {
    levumiLevel: {
      level: 1,
      subject: 1,
    },
    levumiMathCategory: 1,
  },
};

/**
 * @element campus-page-student-knowledge-diagnostik-result-info
 */
@customElement('campus-page-student-knowledge-diagnostik-result-info')
export class CampusPageStudentKnowledgeDiagnostikResultInfo extends ChemistryLitElement {
  @property({ attribute: false }) public user = new RXController(this, new Student({}));

  @state() private readonly _lastResultGerman = new RXController(this, this.user.observable, $student =>
    $student.pipe(
      switchMap(student =>
        getLatestExerciseResultForUser(student._id, ExerciseType.LEVUMIGERMAN)(eventlogSelectionSet),
      ),
    ),
  );
  @state() private readonly _lastResultMath = new RXController(this, this.user.observable, $student =>
    $student.pipe(
      switchMap(student => getLatestExerciseResultForUser(student._id, ExerciseType.LEVUMIMATH)(eventlogSelectionSet)),
    ),
  );
  @state() private readonly _lastResultNumberDictate = new RXController(this, this.user.observable, $student =>
    $student.pipe(
      switchMap(student =>
        getLatestExerciseResultForUser(student._id, ExerciseType.LEVUMI_NUMBER_DICTATE)(eventlogSelectionSet),
      ),
    ),
  );

  private _getTypeTranslation(type: number) {
    switch (type) {
      case ExerciseType.LEVUMIGERMAN:
        return 'LEVUMI Deutsch';
      case ExerciseType.LEVUMIMATH:
        return 'LEVUMI Mathe';
      case ExerciseType.LEVUMI_NUMBER_DICTATE:
        return 'LEVUMI Zahlendiktat';

      default:
        return '';
    }
  }

  private _getLatest() {
    const all = [
      this._lastResultGerman.value?.[0],
      this._lastResultMath.value?.[0],
      this._lastResultNumberDictate.value?.[0],
    ].filter(x => x) as ContentResultEventlog[];
    return all.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0];
  }

  override render() {
    if (this._lastResultGerman.isLoading || this._lastResultMath.isLoading || this._lastResultNumberDictate.isLoading) {
      return html`<cr-flat-loader></cr-flat-loader>`;
    }
    const latest = this._getLatest();

    return html`
      <cr-flex horizontal>
        <cr-text>
          ${this._getTypeTranslation(latest?.type ?? -1)}
          ${latest?.createdAt
            ? DateTime.fromJSDate(latest.createdAt).toFormat('dd.LL.yyyy HH:mm')
            : 'Kein Test gefunden!'}
        </cr-text>
        <cr-tooltip>
          <cr-flex space="md" ${css({ padding: `0 ${this.spacing.sm}` })}>
            <cr-text bold>Letzter Test pro Kategorie</cr-text>

            <cr-grid space="sm" columns="max-content 1fr">
              <cr-flex horizontal space="xs">
                <cr-text bold> LEVUMI Deutsch </cr-text>
                <cr-text> ${this._lastResultMath.value?.[0]?.levumiResultInformation.levumiLevel?.level} </cr-text>
              </cr-flex>
              <cr-text>
                ${this._lastResultGerman.value?.[0]
                  ? `${DateTime.fromJSDate(this._lastResultGerman.value[0].createdAt).toRelative()}`
                  : ''}
                ${this._lastResultGerman.value?.[0]
                  ? `, am ${DateTime.fromJSDate(this._lastResultGerman.value[0].createdAt).toFormat(
                      'dd.LL.yyyy HH:mm',
                    )}`
                  : 'Kein Test gefunden!'}
              </cr-text>
              <cr-flex horizontal space="xs">
                <cr-text bold> LEVUMI Mathe </cr-text>
                <cr-text>
                  ${this._lastResultMath.value?.[0]?.levumiResultInformation.levumiMathCategory}
                  ${this._lastResultMath.value?.[0]?.levumiResultInformation.levumiLevel?.level}
                </cr-text>
              </cr-flex>
              <cr-text>
                ${this._lastResultMath.value?.[0]
                  ? `${DateTime.fromJSDate(this._lastResultMath.value[0].createdAt).toRelative()}`
                  : ''}
                ${this._lastResultMath.value?.[0]
                  ? `, am ${DateTime.fromJSDate(this._lastResultMath.value[0].createdAt).toFormat('dd.LL.yyyy HH:mm')}`
                  : 'Kein Test gefunden!'}
              </cr-text>
              <cr-flex horizontal space="xs">
                <cr-text bold> LEVUMI Zahlendiktat </cr-text>
                <cr-text> ${this._lastResultMath.value?.[0]?.levumiResultInformation.levumiLevel?.level} </cr-text>
              </cr-flex>

              <cr-text>
                ${this._lastResultNumberDictate.value?.[0]
                  ? `${DateTime.fromJSDate(this._lastResultNumberDictate.value[0].createdAt).toRelative()}`
                  : ''}
                ${this._lastResultNumberDictate.value?.[0]
                  ? `, am ${DateTime.fromJSDate(this._lastResultNumberDictate.value[0].createdAt).toFormat(
                      'dd.LL.yyyy',
                    )}`
                  : 'Kein Test gefunden!'}
              </cr-text>
            </cr-grid>
          </cr-flex>
        </cr-tooltip>
      </cr-flex>
    `;
  }
}

@customElement('campus-page-student-knowledge-diagnostik')
export class CampusPageStudentKnowledgeDiagnostik extends CrPage {
  @state() private readonly _config = new RXController(
    this,
    ChancenPersonalConfigAPI.getByKeyAndCategory(
      ListUIConfig<typeof Student>,
      'list-students',
      'default',
    )({
      key: 1,
      userID: 1,
      category: 1,
      view: {
        filter: {
          ...ChancenwerkUserFilter.allFields,
          aspects: {
            ...AbstractAspect.allFields,
            value: 1,
          },
        },
        mode: 1,
        configuredColumns: {
          customID: 1,
          fieldName: 1,
          columnWidth: 1,
        },
        selectedItems: 1,
        multiSelection: 1,
      },
    }),
  );

  @state() private readonly _levels = new RXController(
    this,
    CampusLevumiLevel.getAll<CampusLevumiLevel>()(CampusLevumiLevel.allFields),
  );

  constructor() {
    super();
    this.label = 'Auswertung';
    this.description =
      'Hier kannst du die Ergebnisse der Diagnostik deiner Schüler:innen einsehen. In Zukunft werden diese Informationen über ein Teacher-Dashboard angezeigt. WIP';
    this.permission = ChancenPermission.campus.READ_DIAGNOSTIC;
    this.stickyHeader = true;
    this.topScrollButton = true;
    this.noBottomPadding = true;
    this.fixedHeight = true;
  }

  protected override content() {
    if (this._config.isLoading) return LOADER;
    const options = this._getDataViewOptions();
    return html`
      <cr-flex
        ${css({
          height: '100%',
          overflowY: 'auto',
        })}
      >
        ${this.topScrollButton ? html` <cr-flex ${ref(this._anchor)}></cr-flex> ` : nothing}
        <cr-card padding="sm" ${css({ flexShrink: '0', position: 'sticky', left: 0 })} margin="sm">
          <x-filter-editor .options=${options.filterOptions}></x-filter-editor>
        </cr-card>
        <cr-data-view ${css({ paddingTop: this.spacing.sm })} .options=${options.dataViewOptions}> </cr-data-view>
      </cr-flex>
    `;
  }

  private _getDataViewOptions(): {
    dataViewOptions: FullDataViewOptions<typeof Student>;
    filterOptions: FullFilterEditorOptions<typeof Student>;
  } {
    return buildChancenDataViewOptions<typeof Student>(
      {
        class: Student,
        operation: UserAPI.subscribeByFilter,
        view:
          this._config.value?.view ??
          new View({
            filter: new ChancenwerkUserFilter({
              userClass: [Student._class],
              aspects: [
                KPFAspectSchema.aspectConstructor,
                new BooleanAspect({
                  customID: 'active',
                  value: true,
                }),
              ],
              sortingFields: ['partnerName', 'vorname'],
              sortingDirections: ['asc', 'asc'],
            }),
          }),
        isEntryClickable: false,
        fieldSelectionOptions: {
          hideFieldsWithoutTranslation: true,
          shouldIncludeField: shouldIncludeFieldHelpers.bySelectionSet({ blacklist: STUDENT_BLACKLIST }),
        },
        isSortable: true,
        isColumnSelectable: true,
        isColumnResizable: true,
        onlyShowLastTranslation: true,
        customColumns: [
          { fieldName: 'nachname' },
          { fieldName: 'vorname' },
          {
            columnName: 'Schule',
            customID: 'studentSchule',
            getFieldMapping: entity => entity.kpfs?.map(kpf => kpf.school?.name).join(', ') ?? '',
            selectionSet: { kpfs: { school: { name: 1 } } },
          },
          { fieldName: 'schoolInformation.jahrgangsstufe' },
          {
            columnName: 'Letzter CHANCENkurve Test',
            customID: 'resultInfo',
            columnWidth: 'max-content',
            renderItem: entity =>
              html`
                <campus-page-student-knowledge-diagnostik-result-info .user=${entity}>
                </campus-page-student-knowledge-diagnostik-result-info>
              `,
            filterAspect: DateTimeAspectSchema('before'),
          },
          {
            customID: '__actions',
            columnName: '',
            renderItem: entity => html`
              <x-flex horizontal space="sm">
                <x-flex>
                  <cr-button
                    mode="icon"
                    icon="analytics"
                    href="${CampusRoutes.diagnostics}/${entity._id || 'invalid_id'}"
                  >
                    Levumi
                  </cr-button>
                  <cr-tooltip> <cr-text> LEVUMI </cr-text> </cr-tooltip>
                </x-flex>
                <x-flex>
                  <cr-button mode="icon" icon="quiz" href="${CampusRoutes.answers}/${entity._id || 'invalid_id'}">
                    Antworten einsehen
                  </cr-button>
                  <cr-tooltip> <cr-text> Antworten einsehen </cr-text> </cr-tooltip>
                </x-flex>
              </x-flex>
            `,
            isResizable: false,
            isAlwaysVisible: true,
            isSortable: false,
            isSticky: true,
            position: 'front',
            columnWidth: '80px',
          },
          {
            columnName: 'KPFs',
            customID: 'kpfsMultiple',
            getFieldMapping: entity => {
              if (entity instanceof Student) {
                return entity.kpfs?.map(kpf => kpf.kuerzel).join(', ') ?? '';
              }
              return nothing;
            },
            filterAspect: KPFMultipleAspectSchema,
            selectionSet: { kpfs: { produkt: ProductLKK.allFields, school: { name: 1, kuerzel: 1 }, kuerzel: 1 } },
          },
          {
            customID: 'active',
            columnName: 'Aktiv',
            renderItem: entity =>
              !entity.kuendigung.austrittsdatum || entity.kuendigung.austrittsdatum > DateTime.now()
                ? html`
                    <x-flex>
                      <cr-icon> check </cr-icon>
                      <cr-tooltip>
                        <cr-text> Aktiv </cr-text>
                      </cr-tooltip>
                    </x-flex>
                  `
                : html`
                    <x-flex>
                      <cr-icon> close </cr-icon>
                      <cr-tooltip>
                        <cr-text> Inaktiv </cr-text>
                      </cr-tooltip>
                    </x-flex>
                  `,
            selectionSet: { kuendigung: { austrittsdatum: 1 } },
            filterAspect: BooleanAspectSchema,
            columnWidth: '50px',
            isResizable: false,
            position: 'front',
          },
          ...(this._levels.value ?? [])
            .filter(level => !!level.subject)
            .toSorted((a, b) => a.subject.localeCompare(b.subject))
            .flatMap(level => {
              if (level.subject === LevumiSubject.MATH) {
                return Object.keys(LevumiMathCategory).map(k => {
                  const name = LevumiMathCategory[k];
                  return {
                    customID: `levumiLevel${level.subject}${level.level}${k}`,
                    columnName: `LEVUMI Mathe ${{ unspecified: '' }[level.level] ?? level.level} ${name}`,
                    selectionSet: {
                      levumiResults: ContentResultEventlog.allFields,
                    },
                    getFieldMapping: (entity: Student) => {
                      const res = entity.levumiResults?.filter(
                        r =>
                          r.levumiResultInformation?.levumiLevel?.subject === level.subject &&
                          r.levumiResultInformation?.levumiLevel?.level === level.level &&
                          r.levumiResultInformation?.levumiMathCategory === name,
                      );

                      if (!res || res.length === 0) {
                        return nothing;
                      }

                      const latest = res[0];
                      if (!latest) {
                        return nothing;
                      }

                      return html`<cr-flex horizontal crossaxis-center space="sm">
                        <cr-button
                          mode="icon"
                          icon="analytics"
                          href="${CampusRoutes.diagnostics}/${entity._id ||
                          'invalid_id'}?subject=${level.subject}&level=${level.level}&category=${latest
                            .levumiResultInformation.levumiMathCategory}"
                        ></cr-button>
                        <cr-text>
                          Letzter Test: ${DateTime.fromJSDate(latest.createdAt).toFormat('dd.LL.yyyy HH:mm')}
                          (${res.length}. Test)
                        </cr-text>
                      </cr-flex>`;
                    },
                  };
                });
              }

              return [
                {
                  customID: `levumiLevel${level.subject}${level.level}`,
                  columnName: `LEVUMI ${
                    { german: 'Deutsch', math: 'Mathe', number_dictation: 'Zahlendiktat' }[level.subject] ??
                    level.subject
                  } ${{ unspecified: '' }[level.level] ?? level.level}`,
                  selectionSet: {
                    levumiResults: ContentResultEventlog.allFields,
                  },
                  getFieldMapping: (entity: Student) => {
                    const res = entity.levumiResults?.filter(
                      r =>
                        r.levumiResultInformation?.levumiLevel?.subject === level.subject &&
                        r.levumiResultInformation?.levumiLevel?.level === level.level,
                    );

                    if (!res || res.length === 0) {
                      return nothing;
                    }

                    const latest = res[0];
                    if (!latest) {
                      return nothing;
                    }

                    return html`<cr-flex horizontal crossaxis-center space="sm">
                      <cr-button
                        mode="icon"
                        icon="analytics"
                        href="${CampusRoutes.diagnostics}/${entity._id ||
                        'invalid_id'}?subject=${level.subject}&level=${level.level}"
                      ></cr-button>
                      <cr-text>
                        Letzter Test: ${DateTime.fromJSDate(latest.createdAt).toFormat('dd.LL.yyyy HH:mm')}
                        (${res.length}. Test)
                      </cr-text>
                    </cr-flex>`;
                  },
                },
              ];
            }),
        ],
      } as DataViewOptions<typeof Student>,
      {
        fieldAspectGeneration: {
          hideFieldsWithoutTranslation: true,
          shouldIncludeField: shouldIncludeFieldHelpers.bySelectionSet({
            blacklist: {
              ...STUDENT_BLACKLIST,
              contactSourcePerson: 1,
              personalSignOnChrstimasCardFrom: 1,
              schoolInformation: {
                lastChangeOnJahrgangsstufeByUser: 1,
                lastChangeOnJahrgangsstufeByUserID: 1,
              },
              businessAddress: 1,
            },
          }),
        },
        filter: this._config.value?.view?.filter,
        onFilterChanged: async filter => {
          if (!this._config.value?.view) return;
          this._config.value.view.filter = filter;
          if (this._config.value._id) await this._config.value.save();
          else await this._config.value.create();
        },
      } as FilterEditorOptions<typeof Student>,
    );
  }
}
